import { c as _c } from "react/compiler-runtime";
import { removePTag, hasHeadingTag, countPTags } from "@/lib/utils/detectTags";
export type WysiwygMode = "heading" | "body" | "raw";
interface WysiwygProps extends React.HTMLAttributes<HTMLElement> {
  html: string;
  mode: WysiwygMode;
}

/**
 * Renders CMS-authored HTML. `mode` decides how the wrapper tag and any
 * <p> tags in `html` are resolved:
 * - "raw": render `html` untouched in a <span> (non-prose content, or the
 *   caller already owns the surrounding element).
 * - "heading"/"body": if `html` already has its own block structure (a
 *   heading tag, or more than one <p>), it is left untouched in a <div> so
 *   real paragraph breaks or headings are never merged. Otherwise any single
 *   wrapping <p> is stripped and the content is rendered in a <span>
 *   (heading, so it can sit inside an <h1>-<h5>) or a <p> (body).
 */
export const Wysiwyg: React.FC<WysiwygProps> = t0 => {
  const $ = _c(26);
  let html;
  let mode;
  let props;
  if ($[0] !== t0) {
    ({
      html,
      mode,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = html;
    $[2] = mode;
    $[3] = props;
  } else {
    html = $[1];
    mode = $[2];
    props = $[3];
  }
  if (mode === "raw") {
    let t1;
    if ($[4] !== html) {
      t1 = {
        __html: html
      };
      $[4] = html;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== props || $[7] !== t1) {
      t2 = <span dangerouslySetInnerHTML={t1} {...props} />;
      $[6] = props;
      $[7] = t1;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  const hasOwnBlockStructure = hasHeadingTag(html) || countPTags(html) > 1;
  if (hasOwnBlockStructure) {
    let t1;
    if ($[9] !== html) {
      t1 = {
        __html: html
      };
      $[9] = html;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    let t2;
    if ($[11] !== props || $[12] !== t1) {
      t2 = <div dangerouslySetInnerHTML={t1} {...props} />;
      $[11] = props;
      $[12] = t1;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    return t2;
  }
  let t1;
  if ($[14] !== html) {
    t1 = removePTag(html);
    $[14] = html;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  const cleanedHtml = t1;
  if (mode === "heading") {
    let t2;
    if ($[16] !== cleanedHtml) {
      t2 = {
        __html: cleanedHtml
      };
      $[16] = cleanedHtml;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    let t3;
    if ($[18] !== props || $[19] !== t2) {
      t3 = <span dangerouslySetInnerHTML={t2} {...props} />;
      $[18] = props;
      $[19] = t2;
      $[20] = t3;
    } else {
      t3 = $[20];
    }
    return t3;
  }
  let t2;
  if ($[21] !== cleanedHtml) {
    t2 = {
      __html: cleanedHtml
    };
    $[21] = cleanedHtml;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  let t3;
  if ($[23] !== props || $[24] !== t2) {
    t3 = <p dangerouslySetInnerHTML={t2} {...props} />;
    $[23] = props;
    $[24] = t2;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  return t3;
};